import {
  Form,
  InputTextArea,
  InputLengthIndicator,
} from "@sellar-io/components";
import {
  OrderCommentCreateRequestType,
  OrderCommentCreateResponseType,
} from "@sellar-io/api-v2/types";
import { UseMutationResult } from "@tanstack/react-query";
import { ReactElement, useId, useState } from "react";
import { useEvents, useSellarUser } from "../../hooks";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { BasicDialog } from "../modal/BasicDialog";
import { Alert } from "../alert/Alert";
import { InputContainer } from "@sellar-io/components";
import { apiV2AssertError } from "../../hooks/api/v2/apiV2Request";

export type CommentDialogType = {
  orderId: number;
  active: boolean;
  onDismiss: () => void;
  type: "comment" | "message";
  mutation: UseMutationResult<
    OrderCommentCreateResponseType,
    unknown,
    OrderCommentCreateRequestType
  >;
  onSuccess?: () => void;
};
export const CommentDialog = ({
  orderId,
  active,
  onDismiss,
  type,
  mutation,
  onSuccess,
}: CommentDialogType): ReactElement => {
  const { user } = useSellarUser();
  const { track } = useEvents();
  const { business } = useSellarCurrentBusiness();

  const [newComment, setNewComment] = useState("");
  const formId = useId();

  return (
    <BasicDialog
      title={`Add ${type}`}
      dialogActive={active}
      loading={mutation.isPending}
      onDismiss={onDismiss}
      confirmFormId={formId}
      confirmButtonText="confirm"
      dismissButtonText="dismiss"
    >
      <Form
        id={formId}
        className="flex w-full flex-col"
        onSubmit={() => {
          mutation.mutate(
            {
              OrderId: orderId,
              message: newComment,
            },
            {
              onSuccess: () => {
                setNewComment("");
                onDismiss();
                onSuccess?.();
                track("Comment Created", {
                  businessName: business?.name,
                  message: newComment,
                  kind: "comment",
                });
              },
            },
          );
        }}
      >
        {mutation.isError && (
          <Alert
            type={"error"}
            message={apiV2AssertError(mutation.error)?.message ?? ""}
          />
        )}
        <div className={"text-xs"}>
          Commenting as <b>{user?.name}</b>
        </div>
        <InputContainer>
          <InputTextArea
            variant="legacyFixed"
            maxLength={900}
            required={true}
            placeholder={`Write your ${type} here...`}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
          />
        </InputContainer>
        <InputLengthIndicator
          currentLength={newComment.length}
          maxLength={900}
        />
      </Form>
    </BasicDialog>
  );
};
